import { CodeInfo, BillingCodesState } from 'src/context/reducers';
import { Code, LanguageNode } from 'src/api/redux';

export const mapContextToCodeType = (
  contextType: string | undefined
): string | null => {
  if (!contextType) return null;
  const lower = contextType.toLowerCase();
  // Legacy abbreviated aliases
  if (lower === 'icd') return 'ICD10D';
  if (lower === 'icdp') return 'ICD10P';
  return contextType.toUpperCase();
};

export const isCptOrHcpcs = (contextType: string | undefined): boolean => {
  const lower = contextType?.toLowerCase();
  return lower === 'cpt' || lower === 'hcpcs';
};

export const mapCodeToApiFormat = (code: CodeInfo): Code => {
  const isDrug = code.context_type?.toLowerCase() === 'drug';
  const entry: Code = {
    code_type: mapContextToCodeType(code.context_type),
    code_name: isDrug
      ? (code.code?.toUpperCase() ?? null)
      : (code.code ?? null),
  };
  if (code.modifier) {
    entry.modifier = code.modifier;
  }
  return entry;
};

export const buildCohortLanguage = (
  billingCodes: BillingCodesState
): LanguageNode => ({
  operator: billingCodes.anyAllOperator === 'OR' ? 'OR' : 'AND',
  inverse: false,
  children: billingCodes.groups.map((group) => ({
    operator: (group.operator === 'ANY' ? 'OR' : 'AND') as 'AND' | 'OR',
    inverse: group.inverse,
    children: [] as LanguageNode[],
    codes: group.codes.map(mapCodeToApiFormat),
  })),
  codes: [],
});

interface FilterEntry {
  type: string;
  is_exclude: boolean;
  is_all: boolean;
  values: string[];
}

export const filtersFromCohortLanguage = (
  cohortLanguage: LanguageNode
): FilterEntry[] => {
  const filters: FilterEntry[] = [];
  if (!cohortLanguage) return filters;

  // Process root-level codes (flat structure, e.g. from Claude-generated encounters)
  if (cohortLanguage.codes?.length) {
    const codesByType: Record<string, string[]> = {};
    cohortLanguage.codes.forEach((code) => {
      const codeType = code.code_type ?? 'UNKNOWN';
      if (!codesByType[codeType]) codesByType[codeType] = [];
      codesByType[codeType].push(`${codeType}-${code.code_name}`);
    });

    Object.entries(codesByType).forEach(([type, values]) => {
      filters.push({
        type,
        is_exclude: cohortLanguage.inverse ?? false,
        is_all: cohortLanguage.operator === 'AND',
        values,
      });
    });
  }

  // Process children codes
  cohortLanguage.children?.forEach((child) => {
    if (!child.codes?.length) return;

    const codesByType: Record<string, string[]> = {};
    child.codes.forEach((code) => {
      const codeType = code.code_type ?? 'UNKNOWN';
      if (!codesByType[codeType]) codesByType[codeType] = [];
      codesByType[codeType].push(`${codeType}-${code.code_name}`);
    });

    Object.entries(codesByType).forEach(([type, values]) => {
      filters.push({
        type,
        is_exclude: child.inverse ?? false,
        is_all: child.operator === 'AND',
        values,
      });
    });
  });

  return filters;
};
